import { Activity, Briefcase, Server, Timer, Users } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { KpiCard } from '@/components/ui/KpiCard';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  useContactsPerSource,
  useDashboardKpis,
  useJobsPerHour,
} from '@/hooks/useDispatcher';

const PIE_COLORS = ['#55b88b', '#facc15', '#3b82f6', '#ef4444'];

export default function DashboardPage() {
  const kpis = useDashboardKpis();
  const perHour = useJobsPerHour();
  const perSource = useContactsPerSource();

  const jobsToday = kpis.data?.jobs_today ?? { queued: 0, running: 0, done: 0, failed: 0 };
  const totalJobs = jobsToday.queued + jobsToday.running + jobsToday.done + jobsToday.failed;

  const pieData = [
    { name: 'Done', value: jobsToday.done },
    { name: 'Running', value: jobsToday.running },
    { name: 'Queued', value: jobsToday.queued },
    { name: 'Failed', value: jobsToday.failed },
  ];

  return (
    <div>
      <PageHeader title="Overview" description="Pilotage temps réel du dispatcher" />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <KpiCard
          label="Jobs aujourd'hui"
          value={totalJobs}
          icon={Briefcase}
          color="bg-brand-primary"
          sub={`${jobsToday.running} en cours · ${jobsToday.failed} échoués`}
        />
        <KpiCard
          label="Contacts uniques"
          value={kpis.data?.contacts_today ?? '—'}
          icon={Users}
          color="bg-[#14261E]"
          sub="Aujourd'hui"
        />
        <KpiCard
          label="VMs actives"
          value={kpis.data ? `${kpis.data.vms_up}/${kpis.data.vms_total}` : '—'}
          icon={Server}
          color="bg-blue-500"
        />
        <KpiCard
          label="ETA moyen"
          value={kpis.data ? `${kpis.data.avg_eta_minutes}m` : '—'}
          icon={Timer}
          color="bg-purple-500"
        />
        <KpiCard
          label="Pushes réussis"
          value={kpis.data?.pushes_by_target.reduce((acc, p) => acc + p.count, 0) ?? '—'}
          icon={Activity}
          color="bg-emerald-500"
          sub={kpis.data?.pushes_by_target.map((p) => `${p.target}: ${p.count}`).join(' · ')}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader title="Jobs par heure (24h)" />
          <CardContent>
            {perHour.isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={perHour.data ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="hour" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#55b88b" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Statut jobs courants" />
          <CardContent>
            {kpis.isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80}>
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader title="Contacts par source (7j)" />
          <CardContent>
            {perSource.isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={perSource.data ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="source" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#55b88b" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
